<template>
  <div id="taxes-fees-create">
    <SidebarLayout3 :breadcrumb="breadcrumb" :pageTitle="pageTitle">
      <!-- SIDEBAR -->
      <template #sidebar>
        <!-- AÇÕES DA PÁGINA -->
        <SidebarBlock class="page-actions">
          <Heading>
            Ações da página
          </Heading>

          <!-- BOTÃO SALVAR -->
          <Button
            :disabled="disableSaveButton"
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            class="page-actions--button"
            @click="taxesId === null ? createTaxe() : editTaxe()"
          >
            Salvar
          </Button>

          <!-- BOTÃO DESATIVAR -->
          <Button
            v-if="disableDeleteButton"
            variation="error"
            class="page-actions--button"
            @click.stop.prevent="deleteModal(taxeId)"
          >
            Desativar
          </Button>

          <!-- BOTÃO REATIVAR -->
          <Button
            v-if="disableReactivateButton"
            class="page-actions--button"
            @click.stop.prevent="reactivateModal(taxeId)"
          >
            Reativar
          </Button>

          <!-- BOTÃO VOLTAR -->
          <Button
            layout="outline"
            class="page-actions--button"
            @click="
              $router.push({
                path: `/hoteis/taxas/${$route.params.id}`
              })
            "
          >
            Voltar sem salvar
          </Button>
        </SidebarBlock>
      </template>

      <!-- FORMULÁRIO -->
      <template #content>
        <FormInput
          v-model="form.nome"
          :error="error.nome"
          :showErrorFeedbackMessage="true"
          required
          showCounter
          maxlength="24"
          label="Nome"
          placeholder="Insira o nome da taxa"
          class="text-input--name large"
        />

        <!-- SEGUNDA LINHA -->
        <span>
          <!-- INPUTS DE VALOR -->
          <div class="form">
            <PmwFormCurrencyInput
              v-if="form.tipoValor === 2"
              v-model="form.valor"
              :error="error.valor"
              :options="{ precision: 2 }"
              :showErrorFeedbackMessage="true"
              required
              type="number"
              label="Valor"
              placeholder="Insira o valor"
              class="text-input--currency small"
            />

            <FormInput
              v-else
              v-model="form.valor"
              :error="error.valor"
              :showErrorFeedbackMessage="true"
              required
              sufix="%"
              type="number"
              label="Valor"
              placeholder="Insira o valor"
              class="text-input--percentage small"
            />

            <!-- INPUTS - TIPO DO VALOR -->
            <div>
              <!-- LABEL RADIO BUTTONS -->
              <label class="form-field--label">Tipo do valor</label>
              <PmwRequiredSignal />

              <!-- RADIO BUTTONS -->
              <span class="radio-fields">
                <InputRadio
                  v-model="form.tipoValor"
                  :inputValue="1"
                  :checked="form.tipoValor === 1"
                  name="tipoValor-radio"
                  label="Percentual"
                  class="radio-fields--radio"
                />
                <InputRadio
                  v-model="form.tipoValor"
                  :inputValue="2"
                  :checked="form.tipoValor === 2"
                  :label="'Fixo' + ' (' + getCurrencySymbol + ')'"
                  name="tipoValor-radio"
                  class="radio-fields--radio"
                />
              </span>
            </div>

            <!-- INPUTS - TIPO DO MULTIPLICADOR -->
            <div v-if="form.tipoValor === 2">
              <!-- LABEL RADIO BUTTONS -->
              <label class="form-field--label">Tipo do multiplicador</label>
              <PmwRequiredSignal />

              <!-- RADIO BUTTONS -->
              <span class="radio-fields">
                <InputRadio
                  v-model="form.tipoMultiplicador"
                  :inputValue="1"
                  :checked="form.tipoMultiplicador === 1"
                  name="tipoMultiplicador-radio"
                  label="Room Night"
                  class="radio-fields--radio"
                />
                <InputRadio
                  v-model="form.tipoMultiplicador"
                  :inputValue="2"
                  :checked="form.tipoMultiplicador === 2"
                  name="tipoMultiplicador-radio"
                  label="Reserva"
                  class="radio-fields--radio"
                />
              </span>
            </div>
          </div>

          <!-- INPUTS DE TIPO DE TAXA -->
          <div class="form">
            <FormSelect
              v-model="form.tipoTaxa"
              :options="taxeOptions"
              required
              label="Tipo de taxa"
              placeholder="Selecione um tipo de taxa"
              class="text-input--taxa medium"
            />
          </div>
        </span>

        <SeparateLine :left="0" :right="0" :top="40" :bottom="35" />

        <!-- FOOTER -->
        <span class="bottom-buttons">
          <Button
            layout="outline"
            class="bottom-buttons--return"
            @click="
              $router.push({
                path: `/hoteis/taxas/${$route.params.id}`
              })
            "
          >
            Voltar sem salvar
          </Button>
          <Button
            :disabled="disableSaveButton"
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            :popperStrategy="'auto'"
            class="bottom-buttons--save"
            @click="taxesId === null ? createTaxe() : editTaxe()"
          >
            Salvar
          </Button>
        </span>
      </template>
    </SidebarLayout3>

    <!-- MODAL DELEÇÃO -->
    <PmwAlertModal
      v-model="showDeleteModal"
      closeButton
      title="Desabilitar taxa"
      icon="pmw-trash-can"
      iconColor="red-100"
      cancelButtonColor="grey-90"
      confirmButtonColor="red-100"
      confirmButtonText="Sim, quero desativar"
      @alert-modal-cancel="cancelDisable()"
      @alert-modal-confirm="disableTaxes()"
    >
      <template #text>
        Ao desativar essa taxa, ela estará indisponível para uso. <br />
        Você tem certeza de que quer fazer isso?
      </template>
    </PmwAlertModal>

    <!-- MODAL REATIVAÇÃO -->
    <PmwAlertModal
      v-model="showReactivateModal"
      closeButton
      title="Atenção"
      @alert-modal-cancel="cancelReactivate()"
      @alert-modal-confirm="reactivateTaxe()"
    >
      <template #text>
        Ao reativar essa taxa, ela estará disponível para uso. <br />
        Você tem certeza de que quer fazer isso?
      </template>
    </PmwAlertModal>
  </div>
</template>

<script>
/** Componentes da lib */
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'
import Button from '@solucoes/components-lib-tmp/src/components/elements/Button'
import FormSelect from '@solucoes/components-lib-tmp/src/components/elements/FormSelect'
import Heading from '@solucoes/components-lib-tmp/src/components/elements/Heading'
import FormInput from '@solucoes/components-lib-tmp/src/components/elements/FormInput'
import PmwFormCurrencyInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormCurrencyInput'
import SeparateLine from '@solucoes/components-lib-tmp/src/components/elements/SeparateLine'
import InputRadio from '@solucoes/components-lib-tmp/src/components/elements/InputRadio'
import SidebarBlock from '@solucoes/components-lib-tmp/src/components/patterns/SidebarBlock'
import PmwRequiredSignal from '@solucoes/components-lib-tmp/src/components/patterns/PmwRequiredSignal'

/** Imports do projeto */
import SidebarLayout3 from '@/layouts/SidebarLayout3'
import { mapGetters } from 'vuex'
import { capitalizeString } from '@/helpers/string'

export default {
  name: 'TaxeCreate',
  components: {
    Button,
    FormInput,
    FormSelect,
    Heading,
    SidebarLayout3,
    PmwAlertModal,
    SeparateLine,
    InputRadio,
    SidebarBlock,
    PmwRequiredSignal,
    PmwFormCurrencyInput
  },
  data() {
    return {
      /** Opções do select de tipo de taxa */
      taxeOptions: [
        {
          text: 'Outras',
          value: 1
        },
        {
          text: 'ISS',
          value: 2
        },
        {
          text: 'Serviços',
          value: 3
        },
        {
          text: 'Turismo',
          value: 4
        }
      ],

      /** Breadcrumb da página */
      breadcrumb: [],

      /** Guarda tipo de página (Edição/Criação)  */
      routeType: '',

      /** Título da página  */
      pageTitle: null,

      /** Exibição da modal de deleção */
      showDeleteModal: false,

      /** Exibição da modal de reativação */
      showReactivateModal: false,

      /** Exibição da modal de erro */
      disableExclusivaTarifa: false,

      /** Guarda Id para deleção */
      taxeDelete: null,

      /** Guarda Id da taxa */
      taxeId: null,

      /** Controle de erro */
      error: {
        nome: null,
        valor: null
      },

      /** V-model de cadastro/edição */
      form: {
        codigo: null,
        codigoHotel: null,
        ativo: null,
        nome: null,
        valor: null,
        tipoValor: 1,
        tipoMultiplicador: 2,
        multiplicadorPadrao: null,
        tipoTaxa: {
          text: 'Outras',
          value: 1
        }
      }
    }
  },

  computed: {
    /** Verificador para desabilitar/habilitar o botão de salvar */
    disableSaveButton() {
      if (
        this.form.nome !== null &&
        this.form.nome !== '' &&
        this.form.valor !== null &&
        this.form.valor !== '' &&
        this.form.tipoValor !== null &&
        this.form.tipoValor !== '' &&
        this.form.tipoMultiplicador !== null &&
        this.form.tipoMultiplicador !== ''
      ) {
        return false
      }
      return true
    },

    /** Verificador para desabilitar/habilitar o botão de desabilitar */
    disableDeleteButton() {
      if (this.$route.params.taxeId && this.form.ativo === true) {
        return true
      }
      return false
    },

    /** Verificador para desabilitar/habilitar o botão de desabilitar */
    disableReactivateButton() {
      if (this.$route.params.taxeId && this.form.ativo === false) {
        return true
      }
      return false
    },

    /** Retorna o simbolo monetario do hotel */
    ...mapGetters('hotel', ['getCurrencySymbol'])
  },

  watch: {
    /** Define o valor do Tipo Multiplicador pelo Tipo Valor  */
    'form.tipoValor': {
      handler: function(newValue) {
        if (newValue === 1) {
          this.form.tipoMultiplicador = 2
        } else {
          this.form.tipoMultiplicador = this.form.multiplicadorPadrao
        }
      }
    },

    /** Remove o erro do input  */
    'form.nome': {
      handler: function(newValue) {
        if (newValue !== null || newValue !== '') {
          this.error.nome = false
        }
      }
    },

    /** Remove o erro do input  */
    'form.valor': {
      handler: function(newValue) {
        if (newValue !== null || newValue !== '') {
          this.error.valor = false
        }
      }
    }
  },

  async created() {
    /** Requisição de dados de hoteis */
    await this.$hotel.get(this.$route.params.id).then((response) => {
      this.hotel = response.data

      this.breadcrumb = [
        {
          label: 'Letsbook'
        },
        {
          label: 'Hotéis',
          url: '/hoteis',
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: capitalizeString(this.hotel.nome),
          url: `/hoteis/editar/${this.hotel.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: 'Taxas',
          url: `/hoteis/taxas/${this.hotel.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: 'Criar taxa',
          url: '',
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        }
      ]
    })

    /** Verifica se é uma página de edição ou criação */
    this.pageType()

    /** Se for edição, pegar o ID da taxa */
    this.taxesId = this.$route.params.taxeId || null

    if (this.taxeId === null) {
      this.form.tipoMultiplicador = 2
    }
  },

  methods: {
    /** Verifica se é edição ou criação */
    pageType() {
      if (!this.$route.params.taxeId) {
        this.routeType = true
        this.pageTitle = 'Criar taxa'
      } else {
        this.routeType = false
        this.getSingleTaxe()
      }
    },

    /** Retorna dados da taxa */
    async getSingleTaxe() {
      await this.$taxesAndFees
        .getSingleTaxe(this.$route.params.taxeId)
        .then((response) => {
          const results = response.data

          this.breadcrumb = [
            {
              label: 'Letsbook'
            },
            {
              label: 'Hotéis',
              url: '/hoteis',
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            },
            {
              label: this.hotel.nome,
              url: `/hoteis/editar/${this.hotel.codigo}`,
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            },
            {
              label: 'Taxas',
              url: `/hoteis/taxas/${this.hotel.codigo}`,
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            },
            {
              label: `Editando ${results.nome}`,
              url: '',
              isSpa: true /** se deve ser um link para o site novo ou antigo */
            }
          ]

          this.pageTitle = 'Editando ' + results.nome

          /** Define o valor dos v-models */
          this.form = results

          this.form.multiplicadorPadrao = results.tipoMultiplicador

          /** Define o valor do v-model do select de tipo de taxa */
          if (this.form.tipoTaxa === 1) {
            this.form.tipoTaxa = this.taxeOptions[0]
          } else if (this.form.tipoTaxa === 2) {
            this.form.tipoTaxa = this.taxeOptions[1]
          } else if (this.form.tipoTaxa === 3) {
            this.form.tipoTaxa = this.taxeOptions[2]
          } else {
            this.form.tipoTaxa = this.taxeOptions[3]
          }
        })
    },

    /** Cria um novo taxa */
    createTaxe() {
      /** Verificação de campo do nome */
      if (this.form.nome === null || this.form.nome === '') {
        this.error.nome = true
      }

      /** Verificação de campo do valor */
      if (this.form.valor === null || this.form.valor === '') {
        this.error.valor = true
      }

      this.$taxesAndFees
        .createTaxe({
          codigoHotel: parseInt(this.$route.params.id),
          nome: this.form.nome,
          valor: this.form.valor,
          ativo: true,
          tipoValor: this.form.tipoValor,
          tipoMultiplicador: this.form.tipoMultiplicador,
          tipoTaxa: this.form.tipoTaxa.value
        })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Taxa criada com sucesso.'
          })
          this.$router.push({
            path: `/hoteis/taxas/${this.$route.params.id}`
          })
        })
        .catch((err) => {
          this.$bus.$emit('show-alert-chip', {
            message: err.messages[0]
          })
        })
    },

    /** Edição de taxa */
    editTaxe() {
      /** Verificação de campo do nome */
      if (this.form.nome === null || this.form.nome === '') {
        this.error.nome = true
      }

      /** Verificação de campo do valor */
      if (this.form.valor === null || this.form.valor === '') {
        this.error.valor = true
      }

      this.$taxesAndFees
        .editTaxe({
          codigo: parseInt(this.$route.params.taxeId),
          nome: this.form.nome,
          valor: this.form.valor,
          ativo: true,
          tipoValor: this.form.tipoValor,
          tipoMultiplicador: this.form.tipoMultiplicador,
          tipoTaxa: this.form.tipoTaxa.value
        })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Taxa editada com sucesso.'
          })
          this.$router.push({
            path: `/hoteis/taxas/${this.$route.params.id}`
          })
        })
        .catch((err) => {
          this.$bus.$emit('show-alert-chip', {
            message:
              err?.messages?.[0] ||
              'Já existe uma taxa cadastrada com esse nome.'
          })
        })
    },

    /** Desabilitar taxa */
    disableTaxes() {
      this.$taxesAndFees
        .disableTaxes({ taxas: [this.$route.params.taxeId] })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Taxa desativada com sucesso'
          })
        })
      this.$router
        .push({
          path: `/hoteis/taxas/${this.$route.params.id}`
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Erro ao desativar taxa'
          })
        })
    },

    /** Reativar taxa */
    reactivateTaxe() {
      this.$taxesAndFees
        .reactivateTaxe(this.$route.params.taxeId)
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Taxa reativada com sucesso'
          })
          this.$router.push({
            path: `/hoteis/taxas/${this.$route.params.id}`
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Erro ao reativar taxa'
          })
        })
    },

    /** Controle de reativação */
    reactivateModal(taxeId) {
      this.showReactivateModal = true
      this.taxesReactivate = taxeId
    },

    /** Cancela reativação */
    cancelReactivate() {
      this.showReactivateModal = false
      this.taxesReactivate = null
    },

    /** verificar se dado existe */
    ifExists(element) {
      /** se for array */
      let result = null
      if (element && typeof element.value !== 'undefined') {
        result = element.value
        /** se for string */
      } else if (element !== '') {
        result = element
      }
      return result === true ? 1 : result
    },

    /** Controle de deleção */
    deleteModal(taxeId) {
      this.showDeleteModal = true
      this.taxeDelete = taxeId
    },

    /** Controle de cancelamento de deleção */
    cancelDisable() {
      this.showDeleteModal = false
      this.taxeDelete = null
    }
  }
}
</script>

<style lang="scss" scoped>
.large {
  width: 270px;
}
.medium {
  width: 200px;
}
.small {
  width: 110px;
}
.page-actions {
  padding-top: 45px;
  &--button {
    width: 100%;
    cursor: pointer;

    & + .page-actions--button {
      margin-top: 10px;
    }
  }
  &--tip-text {
    color: $pmw-gray-darkest;
    font-size: 14px;
    line-height: 21px;
    max-width: 215px;
  }
}

.text-input {
  &--name {
    margin-bottom: 40px;
    margin-top: 32px;
  }
  &--currency {
    ::v-deep {
      .pmw-form-field--label {
        font-weight: font-weight(weight-medium);
      }

      .pmw-currency-input--field {
        height: 30px;
      }

      .pmw-form-field--asterisk {
        margin-left: 6px;
      }
    }
  }
  &--percentage {
    ::v-deep {
      .input {
        margin-top: -4px;
      }
      .input--sufix-wrapper {
        padding-top: 10px;
      }
      .form-field--label {
        padding-top: 4px;
        z-index: 5;
      }

      .input--field {
        padding-top: 10px;
      }
    }
  }
  &--taxa {
    margin-top: 15px;
  }
  &--limit {
    width: 141px;
  }
}
.form {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.bottom-buttons {
  display: flex;
  justify-content: space-between;
  &--return,
  &--save {
    width: 215px;
  }
}

::v-deep .content--white_card {
  padding: {
    top: 10px;
  }
}
.form-field {
  display: inline-block;
  position: relative;

  &--label {
    display: inline-flex;
    align-items: center;
    margin: 0;
    margin-right: 5px;
    font-size: pmw-font-size('size-medium');
    font-weight: pmw-font-weight('weight-medium');
  }
}
.radio-fields {
  display: flex;

  &--radio {
    margin-top: 22px;
    margin-right: 15px;
    &__info {
      margin-top: 40px;
      margin-left: -15px;
    }
  }
}
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
}
</style>
